/**
 * 프로그램 워크스페이스 ↔ 서버 baseline head 동기화·충돌 감지.
 */

import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import {
  getProgramWorkspaceScopeEntry,
  setProgramWorkspaceScopeBaselineRevision,
} from './programEditWorkspace';

export const PROGRAM_WORKSPACE_SCOPE_STALE_EVENT = 'cp-program-workspace-scope-stale';

export type ProgramWorkspaceScopeStaleDetail = {
  scopeId: string;
  serverRevision: string;
  baselineRevision: string;
};

export function emitProgramWorkspaceScopeStale(detail: ProgramWorkspaceScopeStaleDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROGRAM_WORKSPACE_SCOPE_STALE_EVENT, { detail }));
}

/** 서버 rows 로드 후 baseline revision 기록 (dirty가 아닐 때만). */
export function syncProgramWorkspaceBaselineFromServerRows(
  scopeId: string,
  rows: ReadonlyArray<{ updated_at?: string | null }>
): void {
  if (!scopeId.trim()) return;
  const entry = getProgramWorkspaceScopeEntry(scopeId);
  if (entry?.isDirty) return;
  const revision = buildLearningContentScopeVersion(rows);
  setProgramWorkspaceScopeBaselineRevision(scopeId, revision);
}

/**
 * head와 baseline 비교.
 * - fresh: 동일
 * - auto-refresh: 서버가 더 새롭고 로컬 dirty 없음 → 호출측에서 refresh
 * - conflict: 서버가 더 새롭고 로컬 dirty 있음
 */
export async function compareProgramWorkspaceScopeHead(params: {
  scopeId: string;
  fetchHead: () => Promise<string>;
}): Promise<'fresh' | 'auto-refresh' | 'conflict' | 'skipped'> {
  const scopeId = params.scopeId.trim();
  if (!scopeId) return 'skipped';
  const entry = getProgramWorkspaceScopeEntry(scopeId);
  const baseline = (entry?.baselineRevision ?? '').trim();
  if (!baseline) return 'skipped';

  let head: string;
  try {
    head = await params.fetchHead();
  } catch {
    return 'skipped';
  }
  if (head === baseline) return 'fresh';
  if (entry?.isDirty) {
    emitProgramWorkspaceScopeStale({
      scopeId,
      serverRevision: head,
      baselineRevision: baseline,
    });
    return 'conflict';
  }
  return 'auto-refresh';
}
